import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import TriageLayout from "./TriageLayout";
import TriageEventsAdmin from "./TriageEventsAdmin";
import { Button } from "@/components/ui/button";
import { UserPlus, ClipboardText, Broadcast, CalendarPlus, ChartBar } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { isSupervisorOrAbove } from "@/lib/permissions";

/**
 * Landing page after triage sign-in. Two big action cards — mirrors
 * forms.osmcops.com/dashboard: Create New Patient + View Patients.
 * Supervisor+ also see a Command Post tile for the live wall-board, a
 * "Manage Events" tile for on-site event configuration, and a Reports
 * tile — per BSVAC field policy the same field lead handles all three.
 */
export default function TriageDashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canSeeCommandPost = isSupervisorOrAbove(user);
  const canManageEvents = isSupervisorOrAbove(user);
  const canOpenReports = isSupervisorOrAbove(user);
  const [eventsOpen, setEventsOpen] = useState(false);
  const totalTiles = 2 + (canSeeCommandPost ? 1 : 0) + (canManageEvents ? 1 : 0) + (canOpenReports ? 1 : 0);
  const gridCols = totalTiles >= 5 ? "md:grid-cols-5" : totalTiles === 4 ? "md:grid-cols-4" : totalTiles === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  return (
    <TriageLayout>
      <div className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Dashboard</h1>
        <p className="text-slate-500 text-sm mt-1">Welcome back, {user?.name?.split(" ")[0] || "Provider"}</p>

        <div className={`grid gap-4 mt-6 ${gridCols}`}>
          <div className="surface-grid p-6 bg-white" data-testid="tri-card-new-patient">
            <div className="h-10 w-10 rounded-sm bg-sky-100 text-sky-700 flex items-center justify-center mb-3">
              <UserPlus size={22} weight="bold" />
            </div>
            <div className="font-display font-bold text-lg text-slate-900">Create New Patient</div>
            <div className="text-slate-500 text-sm mt-1">Start a new patient assessment form</div>
            <Button className="mt-4 rounded-sm bg-slate-900 hover:bg-slate-800 text-white w-full" onClick={() => nav("/triage/patient/new")} data-testid="tri-new-patient-btn">
              <UserPlus size={14} weight="bold" className="mr-1" /> New Patient Form
            </Button>
          </div>

          <div className="surface-grid p-6 bg-white" data-testid="tri-card-view-patients">
            <div className="h-10 w-10 rounded-sm bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
              <ClipboardText size={22} weight="bold" />
            </div>
            <div className="font-display font-bold text-lg text-slate-900">View Patients</div>
            <div className="text-slate-500 text-sm mt-1">View, edit and export patient records</div>
            <Button variant="outline" className="mt-4 rounded-sm w-full" onClick={() => nav("/triage/patients")} data-testid="tri-view-patients-btn">
              <ClipboardText size={14} weight="bold" className="mr-1" /> View Records
            </Button>
          </div>

          {canSeeCommandPost && (
            <div className="surface-grid p-6 bg-white" data-testid="tri-card-command-post">
              <div className="h-10 w-10 rounded-sm bg-red-100 text-red-700 flex items-center justify-center mb-3">
                <Broadcast size={22} weight="bold" />
              </div>
              <div className="font-display font-bold text-lg text-slate-900">Command Post</div>
              <div className="text-slate-500 text-sm mt-1">Live wall-board grouped by triage tag</div>
              <Button variant="outline" className="mt-4 rounded-sm w-full" onClick={() => nav("/triage/command-post")} data-testid="tri-command-post-btn">
                <Broadcast size={14} weight="bold" className="mr-1" /> Open Wall-Board
              </Button>
            </div>
          )}

          {canManageEvents && (
            <div className="surface-grid p-6 bg-white" data-testid="tri-card-manage-events">
              <div className="h-10 w-10 rounded-sm bg-amber-100 text-amber-700 flex items-center justify-center mb-3">
                <CalendarPlus size={22} weight="bold" />
              </div>
              <div className="font-display font-bold text-lg text-slate-900">Manage Events</div>
              <div className="text-slate-500 text-sm mt-1">Create events with treatment centers</div>
              <Button variant="outline" className="mt-4 rounded-sm w-full" onClick={() => setEventsOpen(true)} data-testid="tri-manage-events-btn">
                <CalendarPlus size={14} weight="bold" className="mr-1" /> Open Events
              </Button>
            </div>
          )}

          {canOpenReports && (
            <div className="surface-grid p-6 bg-white" data-testid="tri-card-reports">
              <div className="h-10 w-10 rounded-sm bg-indigo-100 text-indigo-700 flex items-center justify-center mb-3">
                <ChartBar size={22} weight="bold" />
              </div>
              <div className="font-display font-bold text-lg text-slate-900">Admin Suite</div>
              <div className="text-slate-500 text-sm mt-1">Reports by event, date, and volume</div>
              <Button variant="outline" className="mt-4 rounded-sm w-full" onClick={() => nav("/triage/reports")} data-testid="tri-reports-btn">
                <ChartBar size={14} weight="bold" className="mr-1" /> Open Reports
              </Button>
            </div>
          )}
        </div>
      </div>
      <TriageEventsAdmin open={eventsOpen} onOpenChange={setEventsOpen} />
    </TriageLayout>
  );
}
